import { API_BASE_URL, ApiError, tokenStore } from './client';

export interface UserOpportunityDocument {
  id: string;
  opportunityId: string;
  title: string;
  description: string | null;
  fileName: string;
  mimeType: string;
  fileSize: number;
  /** Caminho relativo à API (ex.: `/opportunities/.../file`). */
  url: string;
  createdAt: string;
}

export function userOpportunityDocumentFullUrl(relativeUrl: string): string {
  const base = API_BASE_URL.replace(/\/+$/, '');
  const path = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
  return `${base}${path}`;
}

async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = tokenStore.getAccess();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    let errorData: unknown;
    try {
      errorData = await res.json();
    } catch {
      errorData = null;
    }
    const rawMsg = (errorData as Record<string, string>)?.message ?? `HTTP ${res.status}`;
    const message = Array.isArray(rawMsg) ? rawMsg.join('; ') : String(rawMsg);
    throw new ApiError(res.status, message, errorData);
  }
  return res;
}

export const userOpportunityDocumentsApi = {
  list: (opportunityId: string) =>
    authFetch(`/opportunities/${encodeURIComponent(opportunityId)}/user-documents`).then(
      (r) => r.json() as Promise<UserOpportunityDocument[]>
    ),

  upload: (opportunityId: string, input: { title: string; description: string; file: File }) => {
    const form = new FormData();
    form.set('title', input.title.trim());
    if (input.description.trim()) form.set('description', input.description.trim());
    form.set('file', input.file);
    return authFetch(`/opportunities/${encodeURIComponent(opportunityId)}/user-documents`, {
      method: 'POST',
      body: form,
    }).then((r) => r.json() as Promise<UserOpportunityDocument>);
  },

  remove: (opportunityId: string, id: string) =>
    authFetch(
      `/opportunities/${encodeURIComponent(opportunityId)}/user-documents/${encodeURIComponent(id)}`,
      { method: 'DELETE' }
    ).then(() => undefined),

  openFile: async (opportunityId: string, doc: UserOpportunityDocument) => {
    const res = await authFetch(doc.url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
};
